import {
  createBusinessEntityDocument,
  updateBusinessEntityDocument,
} from '@commenda/api';
import { linkDocumentType } from '@commenda/api/drive-view/linkDocumentType';
import {
  FeatureLaunch,
  isDocumentsV2Launched,
  isFeatureLaunched,
} from '@commenda/feature-launches';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FieldWrapper,
  Label,
  LoadingIndicator,
  SingleFileUploader,
  useToggle,
} from '@commenda/kakashi';
import mixpanel, {
  MIXPANEL_SOURCE,
  MIXPANEL_SURFACE_AREA,
} from '@commenda/mixpanel';
import {
  IDocument,
  IDriveViewLinkableFile,
} from '@commenda/types/src/drive-view/types';
import { FileBasic } from '@commenda/types/src/file/types';
import { getFileComponents } from '@commenda/utils';
import { LegacyDocumentType } from '@prisma/client';
import { useEffect, useState } from 'react';
import { useNotification } from '../../../../../hooks/useNotification';
import { ExistingDocumentSelect } from './ExistingDocumentSelect';

enum DocumentSource {
  UPLOAD = 'upload',
  EXISTING = 'existing',
}

const UPLOAD_ACCEPT = {
  'application/pdf': ['.pdf'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
    '.docx',
  ],
  'image/*': [],
};
const UPLOAD_MAX_SIZE_BYTES = 25 * 1024 * 1024;

export const UploadFormationDocumentModal = ({
  isOpen,
  onClose,
  title,
  businessEntityId,
  onSuccess,
  documentType,
  existingDocument,
  companyId,
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  businessEntityId: number;
  onSuccess: () => void;
  documentType: LegacyDocumentType;
  existingDocument?: IDocument;
  companyId: number;
  isEditMode?: boolean;
}) => {
  const [file, setFile] = useState<FileBasic | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showReupload, setShowReupload] = useState(false);
  const [selectedExistingFile, setSelectedExistingFile] =
    useState<IDriveViewLinkableFile | null>(null);

  const { setNotification } = useNotification();

  const canChooseExisting =
    !existingDocument?.id &&
    isDocumentsV2Launched(companyId) &&
    isFeatureLaunched(FeatureLaunch.ENTITY_EXISTING_DOCUMENT_PICKER, companyId);
  const isLinkingExisting = canChooseExisting && selectedExistingFile !== null;

  useEffect(() => {
    setIsEditMode(!!existingDocument?.id);
    setShowReupload(false);
  }, [existingDocument]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedExistingFile(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (existingDocument?.id && !showReupload) {
      setFile({
        fileUrl: existingDocument.fileUrl,
        fileName: existingDocument.fileName,
        documentType: existingDocument.documentSubType,
      });
    }
  }, [existingDocument, showReupload]);

  //Change Edit Mode to false if file is changed
  useEffect(() => {
    if (
      file &&
      existingDocument?.id &&
      file.fileUrl !== existingDocument.fileUrl
    ) {
      setIsEditMode(false);
    }
  }, [file, existingDocument]);

  const isUploading = useToggle(false);

  const handleReupload = () => {
    setShowReupload(true);
    setFile(null);
  };

  const onSelectExistingFile = (selected: IDriveViewLinkableFile | null) => {
    setSelectedExistingFile(selected);
    if (selected) setFile(null);
  };

  const trackDocumentUpdated = (source: DocumentSource) => {
    mixpanel.track('ENTITY_GENERAL_INFO_UPDATED', {
      source: MIXPANEL_SOURCE.ENTITY_MANAGEMENT,
      surface_area: MIXPANEL_SURFACE_AREA.ENTITY_MANAGEMENT,
      business_entity_id: businessEntityId,
      company_id: companyId,
      field: 'formation_document',
      document_type: documentType,
      document_source: source,
      is_edit: !!existingDocument?.id,
    });
  };

  const onLinkExistingDocument = async () => {
    if (!selectedExistingFile) {
      setNotification({
        show: true,
        valid: false,
        title: 'No document selected',
        message: 'Select a document to continue.',
      });
      return;
    }

    isUploading.on();

    const result = await linkDocumentType({
      corporationId: businessEntityId,
      fileId: selectedExistingFile.fileId,
      documentType,
    });

    if (!result.success) {
      setNotification({
        show: true,
        valid: false,
        title: "Couldn't add the document",
        message: result.error ?? 'Something went wrong. Please try again.',
      });
      isUploading.off();
      return;
    }

    setNotification({
      show: true,
      valid: true,
      title: 'Document added',
      message: `${selectedExistingFile.fileName} is now this entity's ${title}.`,
    });

    trackDocumentUpdated(DocumentSource.EXISTING);
    setSelectedExistingFile(null);

    await onSuccess();
    isUploading.off();
  };

  const onSubmit = async () => {
    if (isLinkingExisting) {
      await onLinkExistingDocument();
      return;
    }

    if (!file) {
      setNotification({
        show: true,
        valid: false,
        title: 'No file selected',
        message: 'Upload a file to continue.',
      });
      return;
    }

    isUploading.on();

    const response = existingDocument?.id
      ? await updateBusinessEntityDocument(
          businessEntityId,
          +existingDocument.id,
          {
            fileUrl: file.fileUrl,
            fileName: file.fileName,
          }
        )
      : await createBusinessEntityDocument(businessEntityId, {
          documentSubType: documentType,
          fileUrl: file.fileUrl,
          fileName: file.fileName,
        });

    if (!response.ok) {
      setNotification({
        show: true,
        valid: false,
        title: "Couldn't upload the file",
        message: 'Something went wrong. Please try again.',
      });
      isUploading.off();
      return;
    }

    setFile(null);
    setShowReupload(false);
    setNotification({
      show: true,
      valid: true,
      title: existingDocument?.id ? 'Document updated' : 'Document added',
      message: `${file.fileName} was uploaded.`,
    });

    trackDocumentUpdated(DocumentSource.UPLOAD);

    await onSuccess();
    isUploading.off();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className={canChooseExisting ? 'max-w-[676px]' : undefined}
      >
        <DialogHeader className="border-b-0">
          <DialogTitle>
            {isEditMode ? 'Edit' : 'Add'} {title}
          </DialogTitle>
        </DialogHeader>
        <div className="flex min-w-0 flex-col gap-xl px-3xl pb-3xl">
          {canChooseExisting ? (
            <>
              <FieldWrapper>
                <Label>Upload a file</Label>
                <SingleFileUploader
                  file={file}
                  setFile={setFile}
                  onFileChange={() => setSelectedExistingFile(null)}
                  accept={UPLOAD_ACCEPT}
                  maxSize={UPLOAD_MAX_SIZE_BYTES}
                  message="PDF, DOCX or an image, up to 25 MB"
                  companyId={companyId}
                />
              </FieldWrapper>
              <div className="flex items-center gap-lg">
                <div className="h-px min-w-0 flex-1 bg-border-secondary" />
                <span className="txt-extra-small-regular text-text-tertiary">
                  or
                </span>
                <div className="h-px min-w-0 flex-1 bg-border-secondary" />
              </div>
              <FieldWrapper>
                <Label>Select from Documents</Label>
                <ExistingDocumentSelect
                  corporationId={businessEntityId}
                  documentType={documentType}
                  documentLabel={title}
                  selectedFile={selectedExistingFile}
                  onSelect={onSelectExistingFile}
                />
              </FieldWrapper>
            </>
          ) : (
            <FieldWrapper>
              <Label>File</Label>
              {existingDocument?.id && !showReupload ? (
                <div className="space-y-2">
                  <div className="flex min-w-0 items-center gap-2 rounded-lg border p-3">
                    {(() => {
                      const { fileTypeIcon: FileTypeIcon } = getFileComponents(
                        existingDocument.fileName
                      );
                      return (
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded">
                          <FileTypeIcon className="h-8 w-8" />
                        </div>
                      );
                    })()}
                    <span
                      className="max-w-full min-w-0 flex-1 truncate text-sm text-gray-700"
                      title={existingDocument.fileName}
                    >
                      {existingDocument.fileName}
                    </span>
                    <Button
                      variant="secondary"
                      size="sm"
                      className="shrink-0"
                      onClick={handleReupload}
                    >
                      Reupload
                    </Button>
                  </div>
                </div>
              ) : (
                <SingleFileUploader
                  file={file}
                  setFile={setFile}
                  companyId={companyId}
                />
              )}
            </FieldWrapper>
          )}
        </div>
        <DialogFooter>
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={
              isUploading.value ||
              (isEditMode && !showReupload) ||
              (canChooseExisting && !file && !selectedExistingFile)
            }
            onClick={onSubmit}
          >
            {isUploading.value ? <LoadingIndicator size={16} /> : null}
            <span>{existingDocument?.id ? 'Save' : 'Add document'}</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
